import React, {lazy, useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {fetchJson} from '../../../helpers/fetch.js';
import ColumnResizer from '../../common/column_resizer.jsx';
import styles from '../OptionManagement.module.scss';
import withSuspense from '../../../helpers/withSuspense.jsx';
import ButtonSkeleton from '../../../skeleton_loaders/common/ButtonSkeleton.jsx';
import queryClient from '../../../../query_client.js';
import { FaIcon } from '../../common/Icons.jsx';
import HierarchyBuilder from './HierarchyBuilder.jsx';
const NewHierarchyButton = withSuspense(
  lazy(() => import('./NewHierarchyButton.jsx')),
  <ButtonSkeleton size='sm' style={{width: '100%'}} />
);
const EditHierarchyCog = withSuspense(lazy(() => import('./EditHierarchyCog.jsx')), <FaIcon type='cog' />);

import {
  option_set_hierarchy_list_json_path
} from '~/routes';

function Hierarchies({projectId, hierarchies, selectedHierarchyId, setSelectedHierarchyId, permissions}) {
  useEffect(() => {
    if (!hierarchies.length) {
      setSelectedHierarchyId(null);
      return;
    }

    if (!hierarchies.some((hierarchy) => hierarchy.id === selectedHierarchyId)) {
      setSelectedHierarchyId(hierarchies[0].id);
    }
  }, [hierarchies, selectedHierarchyId, setSelectedHierarchyId]);

  return (
    <div className={styles['option-sets-container']}>
      <div className={styles['actions']}>
        {permissions.create_hierarchy && <NewHierarchyButton projectId={projectId} />}
      </div>
      {hierarchies.length ? <hr style={{margin: 0, marginBottom: '1rem'}} /> : null}
      <ul className={styles['list-group']}>
        {hierarchies.map((hierarchy) => (
          <button
            key={hierarchy.id}
            className={`${styles['list-group-item']} list-group-item-action text-start cus ${selectedHierarchyId === hierarchy.id ? styles['active'] : ''}`}
            onClick={() => setSelectedHierarchyId(hierarchy.id)}
          >
            <div className={styles['info']}>
              <div>{hierarchy.name}</div>
              <small className={selectedHierarchyId === hierarchy.id ? '' : 'text-muted'}>
                {hierarchy.key}
              </small>
            </div>
            <div className={styles['option-set-actions']}>
              {permissions.update_hierarchy && <EditHierarchyCog projectId={projectId} hierarchyId={hierarchy.id} />}
            </div>
          </button>
        ))}
      </ul>
    </div>
  );
}

function HierarchyManagement({projectId, permissions = {}}) {
  const [selectedHierarchyId, setSelectedHierarchyId] = useState(null);

  const {data} = useQuery({
    queryKey: ['hierarchies', projectId],
    queryFn: () => fetchJson(option_set_hierarchy_list_json_path())
  });

  const hierarchies = data?.hierarchies || [];

  const hierarchy = useMemo(() => {
    return hierarchies.find((row) => row.id === selectedHierarchyId) || null;
  }, [hierarchies, selectedHierarchyId]);
  const rightWidth = Math.max(window.innerWidth * 0.3, 300);
  const initialLeftWidth = window.innerWidth - rightWidth;

  return (
    <div className={styles['option-management-container']} style={{'--action-bar-height': '47.6px'}}>
      <ColumnResizer
        minLeftWidth={100}
        minRightWidth={100}
        initialLeftWidth={initialLeftWidth}
      >
        {hierarchy ? <HierarchyBuilder projectId={projectId} hierarchy={hierarchy} /> : null}
        <Hierarchies
          projectId={projectId}
          hierarchies={hierarchies}
          selectedHierarchyId={selectedHierarchyId}
          setSelectedHierarchyId={setSelectedHierarchyId}
          permissions={permissions}
        />
      </ColumnResizer>
    </div>
  );
}

export default HierarchyManagement;